import React, { useEffect, useState } from "react";
import { IonPage, IonContent, IonHeader, IonToolbar, IonTitle } from "@ionic/react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format, subDays } from "date-fns";
import { useStudent } from "../contexts/StudentContext";
import { getCatalog } from "../services/contentService";
import { subscribeActivity, DayActivity, xpForLevel } from "../services/progressService";
import { getOwnedBadges, BADGES } from "../services/badges";
import type { Catalog } from "../content/types";
import { subjectCompletion, chaptersForYear } from "../utils/progressStats";
import { dayKey } from "../utils/dates";
import ProgressBar from "../components/ProgressBar";

const Progress: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { user, profile, stats, progress } = useStudent();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [activity, setActivity] = useState<Record<string, DayActivity>>({});
  const [badgeCount, setBadgeCount] = useState(0);

  useEffect(() => {
    getCatalog().then(setCatalog).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (!user) return;
    getOwnedBadges(user.uid).then((b) => setBadgeCount(Object.keys(b).length));
    return subscribeActivity(user.uid, setActivity);
  }, [user, stats.xp]);

  if (!profile) return null;
  const year = profile.year;
  const days = Array.from({ length: 7 }, (_, i) => dayKey(subDays(new Date(), 6 - i)));
  const minutes = days.map((d) => Math.round((activity[d]?.seconds || 0) / 60));
  const maxMin = Math.max(profile.dailyGoalMinutes, ...minutes, 1);
  const nextXp = xpForLevel(stats.level + 1);
  const prevXp = xpForLevel(stats.level);
  const successRate = stats.totalExercises ? Math.round((stats.totalCorrect / stats.totalExercises) * 100) : 0;
  const chapters = catalog ? chaptersForYear(catalog, year) : [];

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonTitle>{t("progress.title")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="page-container wide px-4 py-3 space-y-4 animate-fade-in lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0 lg:items-start">
          <div className="grid grid-cols-3 gap-2 lg:col-span-2">
            <div className="card p-3 text-center">
              <div className="text-2xl font-black text-white">{stats.level}</div>
              <div className="text-[11px] text-gray-400">{t("progress.level")}</div>
            </div>
            <div className="card p-3 text-center">
              <div className="text-2xl font-black text-orange-300">
                <i className="fa-solid fa-fire text-lg mr-1" />
                {stats.streak.current}
              </div>
              <div className="text-[11px] text-gray-400">{t("progress.streakDays")}</div>
            </div>
            <button type="button" onClick={() => history.push("/home/badges")} className="card p-3 text-center">
              <div className="text-2xl font-black text-accent-300">
                {badgeCount}/{BADGES.length}
              </div>
              <div className="text-[11px] text-gray-400">{t("progress.badges")}</div>
            </button>
          </div>

          <div className="card p-4 lg:col-span-2">
            <div className="flex justify-between text-sm mb-1">
              <span className="font-bold text-white">{t("progress.xpToNext", { level: stats.level + 1 })}</span>
              <span className="text-gray-400">
                {stats.xp} / {nextXp}
              </span>
            </div>
            <ProgressBar value={((stats.xp - prevXp) / Math.max(1, nextXp - prevXp)) * 100} />
          </div>

          <div className="card p-4">
            <p className="font-bold text-white mb-3">{t("progress.week")}</p>
            <div className="flex items-end justify-between gap-1 h-28">
              {days.map((d, i) => {
                const h = Math.round((minutes[i] / maxMin) * 100);
                const goalHit = minutes[i] >= profile.dailyGoalMinutes;
                return (
                  <div key={d} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                    <span className="text-[10px] text-gray-400">{minutes[i]}</span>
                    <div className={`w-full rounded-t-md ${goalHit ? "bg-success-500" : "bg-primary-500/70"}`} style={{ height: `${Math.max(4, h)}%` }} />
                    <span className="text-[10px] text-gray-500">{["L", "M", "M", "J", "V", "S", "D"][(new Date(d).getDay() + 6) % 7]}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-gray-500 mt-2">{t("progress.weekHint", { goal: profile.dailyGoalMinutes })}</p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="card p-3 text-center">
              <div className="text-xl font-black text-white">{stats.totalCorrect}</div>
              <div className="text-[11px] text-gray-400">{t("progress.exercisesDone")}</div>
            </div>
            <div className="card p-3 text-center">
              <div className="text-xl font-black text-white">{successRate} %</div>
              <div className="text-[11px] text-gray-400">{t("progress.successRate")}</div>
            </div>
            <div className="card p-3 text-center">
              <div className="text-xl font-black text-white">{stats.totalTests}</div>
              <div className="text-[11px] text-gray-400">{t("progress.testsDone")}</div>
            </div>
          </div>

          {catalog && (
            <div className="card p-4 space-y-3">
              <p className="font-bold text-white">{t("progress.bySubject", { year })}</p>
              {catalog.subjects.map((s) => {
                const pct = subjectCompletion(catalog, progress, s.id, year);
                return (
                  <div key={s.id}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-200">{s.name}</span>
                      <span className="text-gray-400">{pct} %</span>
                    </div>
                    <ProgressBar value={pct} color={s.color} height={6} />
                  </div>
                );
              })}
            </div>
          )}

          {chapters.length > 0 && (
            <div className="card p-4">
              <p className="font-bold text-white mb-2">{t("progress.chapters")}</p>
              <div className="space-y-2">
                {chapters.map((c) => {
                  const p = progress[c.id];
                  const due = p?.review?.nextDueAt;
                  const dueNow = typeof due === "number" && due <= Date.now();
                  return (
                    <button key={c.id} type="button" onClick={() => history.push(`/home/chapter/${c.id}`)} className="w-full flex items-center gap-3 text-left">
                      <span className={`w-2 h-2 rounded-full ${p?.completion >= 100 ? "bg-success-500" : p ? "bg-primary-400" : "bg-white/20"}`} />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-gray-200 truncate">{c.title}</span>
                        {typeof due === "number" && (
                          <span className={`block text-[11px] ${dueNow ? "text-accent-300" : "text-gray-500"}`}>
                            <i className="fa-solid fa-brain mr-1" />
                            {dueNow ? t("review.dueNow") : t("review.nextReview", { date: format(due, "dd.MM.yyyy") })}
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-gray-400">
                        {p?.bestTestScore !== undefined ? `${t("progress.test")} ${p.bestTestScore} % · ` : ""}
                        {p?.completion || 0} %
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="h-2" />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Progress;
